import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import PasteListFlowHeader from "../components/PasteListFlowHeader";
import InfiniteScrollSentinel from "../components/InfiniteScrollSentinel";
import ListFetchError from "../components/ListFetchError";
import CatalogFilterButton from "../components/CatalogFilterButton";
import {
  getInfiniteScrollNextRequest,
  hasMoreInfiniteItems,
} from "../utils/infiniteScrollPaging";
import useProductListInfiniteScroll from "../hooks/useProductListInfiniteScroll";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchActowizFeatures } from "../store/actowiz/actowizSlice";
import { fetchCurrentUser, updateBasketComparisonUsage } from "../store/auth/authSlice";
import { apiRequest } from "../api/client";
import { useToast } from "../components/ToastProvider";
import { rememberGroceryProductView } from "../utils/groceryProductView";
import { buildGroceryAddToListBody, preparePasteListProductsForBasket } from "../utils/groceryPlatformData";
import {
  expandPasteLines,
  fetchGroceryBasketSummary,
  fetchGroceryListDetailProducts,
  groceryListIdOf,
  loadPasteListFlow,
  normalizeGroceryListsMinimal,
  savePasteListFlow,
  toQueryListParam,
} from "../utils/pasteListFlow";
import { PRICE_MODE_OPTIONS, priceModeChipClass } from "../utils/catalogFilters";
import {
  toggleCatalogCategorySelection,
} from "../utils/catalogListFilterOptions";
import { joinCatalogFilterValues } from "../utils/catalogTabState";
import { useStableCatalogFilterOptions } from "../hooks/useStableCatalogFilterOptions";

function resolvePasteListPriceMode(value) {
  return value === "offer_loyalty" ? "offer_loyalty" : "base";
}

function toFilterSet(value) {
  if (Array.isArray(value)) {
    return new Set(value.map((x) => String(x || "").trim()).filter(Boolean));
  }
  return new Set();
}

function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getBasketComparisonUsage(profile) {
  if (!profile || typeof profile !== "object") return null;

  const source =
    profile.basket_comparison ||
    profile.basket_comparisons ||
    profile.comparison_usage ||
    profile;

  const unlimited = source.unlimited === true;
  let used = toCount(source.used);
  let remaining = toCount(source.remaining);
  let monthlyLimit = toCount(source.monthly_limit ?? source.monthlyLimit);

  if (remaining == null && monthlyLimit != null && used != null) {
    remaining = Math.max(0, monthlyLimit - used);
  }
  if (monthlyLimit == null && used != null && remaining != null) {
    monthlyLimit = used + remaining;
  }

  return { unlimited, used, remaining, monthlyLimit };
}

function formatComparisonCount(value) {
  return value == null ? "0" : String(value);
}

function PasteListReview({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const token = useAppSelector((s) => s.auth.token);
  const user = useAppSelector((s) => s.auth.user);
  const authStatus = useAppSelector((s) => s.auth.status);
  const items = useAppSelector((s) => s.actowiz.features);
  const featuresTotal = useAppSelector((s) => s.actowiz.featuresTotal);
  const status = useAppSelector((s) => s.actowiz.featuresStatus);
  const error = useAppSelector((s) => s.actowiz.featuresError);
  const featuresLastBatchSize = useAppSelector((s) => s.actowiz.featuresLastBatchSize);
  const featuresFetchedTotal = useAppSelector((s) => s.actowiz.featuresFetchedTotal);
  const allCatalog = useAppSelector((s) => s.actowiz.allCatalog);
  const searchCatalog = useAppSelector((s) => s.actowiz.searchCatalog);

  const normalizedToken = typeof token === "string" ? token.trim() : token;
  const isLoggedIn =
    Boolean(normalizedToken) &&
    normalizedToken !== "null" &&
    normalizedToken !== "undefined";

  const [lines, setLines] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(() => new Set());
  const [selectedSubcategories, setSelectedSubcategories] = useState(() => new Set());
  const [categoryOpen, setCategoryOpen] = useState(true);
  const [subCategoryOpen, setSubCategoryOpen] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerOptions, setListPickerOptions] = useState([]);
  const [listPickerResolver, setListPickerResolver] = useState(null);
  const [listPickerTitle, setListPickerTitle] = useState("Choose Grocery List");
  const [pendingGroceryListId, setPendingGroceryListId] = useState(null);
  const [compareConfirmOpen, setCompareConfirmOpen] = useState(false);
  const [compareSubmitting, setCompareSubmitting] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [limitModalMessage, setLimitModalMessage] = useState("");
  const [priceMode, setPriceMode] = useState(() =>
    resolvePasteListPriceMode(loadPasteListFlow()?.priceMode)
  );
  const [flowReady, setFlowReady] = useState(false);

  const prevFilterKeyRef = useRef("");
  const prevQueryListRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const isLoadingMoreRef = useRef(false);

  const isOfferMode = priceMode === "offer_loyalty";

  const queryList = useMemo(() => {
    if (!lines.length) return "";
    return toQueryListParam(lines.join("\n"));
  }, [lines]);

  const queryListKey = useMemo(() => queryList.trim().toLowerCase(), [queryList]);

  const catalogForFilters = useMemo(() => {
    if (
      searchCatalog?.queryKey === queryListKey &&
      Array.isArray(searchCatalog.categories) &&
      searchCatalog.categories.length > 0
    ) {
      return searchCatalog;
    }
    return allCatalog;
  }, [searchCatalog, queryListKey, allCatalog]);

  const filterKey = useMemo(
    () =>
      [
        queryList,
        joinCatalogFilterValues(selectedCategories),
        joinCatalogFilterValues(selectedSubcategories),
        isOfferMode ? "offer" : "base",
      ].join("|"),
    [queryList, selectedCategories, selectedSubcategories, isOfferMode]
  );

  const displayItems = useMemo(
    () =>
      (items || []).filter(
        (p) => p?.title && Array.isArray(p?.comparison) && p.comparison.length > 0
      ),
    [items]
  );

  const preparedDisplayItems = useMemo(
    () => preparePasteListProductsForBasket(displayItems),
    [displayItems]
  );

  const rawItemsCount = (items || []).length;
  const totalProductsCount =
    typeof featuresTotal === "number" && featuresTotal >= 0
      ? featuresTotal
      : displayItems.length;

  const showProductGrid = displayItems.length > 0;
  const profileLoading = isLoggedIn && !user && authStatus === "loading";
  const canStartComparison = user?.allowed === true && user?.can_compare === true;
  const comparisonLimitMessage =
    "Monthly basket comparison limit reached. Try again next month or upgrade your plan.";
  const isBasketSummaryDisabled = isLoggedIn && Boolean(user) && !canStartComparison;
  const comparisonUsage = useMemo(() => getBasketComparisonUsage(user), [user]);

  const isInitialLoading = status === "loading" && rawItemsCount === 0;
  const isLoadingMore = status === "loading" && rawItemsCount > 0;
  const isLoadMoreError = Boolean(error) && displayItems.length > 0;
  const hasMore = hasMoreInfiniteItems({ lastBatchSize: featuresLastBatchSize });

  const handlePriceModeChange = useCallback((mode) => {
    const nextMode = resolvePasteListPriceMode(mode);
    setPriceMode(nextMode);
    const flow = loadPasteListFlow();
    if (flow) {
      savePasteListFlow({
        ...flow,
        priceMode: nextMode,
      });
    }
  }, []);

  const fetchFeatures = useCallback(
    ({ apiLoadedCount = 0, isRefresh = false } = {}) => {
      if (!queryList) return;
      const { start, offset, mode } = getInfiniteScrollNextRequest({ apiLoadedCount, isRefresh });
      dispatch(
        fetchActowizFeatures({
          query_list: queryList,
          category: selectedCategories.size ? Array.from(selectedCategories) : undefined,
          sub_category: selectedSubcategories.size ? Array.from(selectedSubcategories) : undefined,
          offer: isOfferMode || undefined,
          start,
          offset,
          mode,
          force: true,
        })
      );
    },
    [dispatch, queryList, selectedCategories, selectedSubcategories, isOfferMode]
  );

  useEffect(() => {
    if (!flowReady) return;
    if (prevQueryListRef.current === null) {
      prevQueryListRef.current = queryList;
      return;
    }
    if (prevQueryListRef.current === queryList) return;
    prevQueryListRef.current = queryList;
    prevFilterKeyRef.current = "";
    setSelectedCategories(new Set());
    setSelectedSubcategories(new Set());
  }, [queryList, flowReady]);

  useEffect(() => {
    if (!flowReady || !queryList) return;
    if (prevFilterKeyRef.current === filterKey) return;
    prevFilterKeyRef.current = filterKey;
    loadingMoreRef.current = false;
    isLoadingMoreRef.current = false;
    fetchFeatures({ isRefresh: true });
  }, [filterKey, fetchFeatures, flowReady, queryList]);

  const loadMore = useCallback(() => {
    if (status === "loading" || !hasMore || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    isLoadingMoreRef.current = true;
    fetchFeatures({ apiLoadedCount: featuresFetchedTotal, isRefresh: false });
  }, [status, hasMore, featuresFetchedTotal, fetchFeatures]);

  const retryFetch = useCallback(() => {
    if (status === "loading") return;
    loadingMoreRef.current = false;
    isLoadingMoreRef.current = false;
    if (rawItemsCount === 0) {
      fetchFeatures({ isRefresh: true });
      return;
    }
    fetchFeatures({ apiLoadedCount: featuresFetchedTotal, isRefresh: false });
  }, [status, rawItemsCount, featuresFetchedTotal, fetchFeatures]);

  useEffect(() => {
    if (status !== "loading") {
      loadingMoreRef.current = false;
      isLoadingMoreRef.current = false;
    }
  }, [status]);

  const { bindPrefetchNode, prefetchIndex } = useProductListInfiniteScroll({
    itemCount: displayItems.length,
    hasMore: hasMore && !error,
    isLoading: status === "loading",
    onLoadMore: loadMore,
  });

  useEffect(() => {
    if (status === "loading" || !hasMore || error) return;
    if (displayItems.length === 0 && rawItemsCount > 0) {
      loadMore();
    }
  }, [status, hasMore, error, displayItems.length, rawItemsCount, loadMore]);

  const resetFilters = () => {
    setSelectedCategories(new Set());
    setSelectedSubcategories(new Set());
  };

  const { categoryOptions, subcategoryOptions } = useStableCatalogFilterOptions(
    catalogForFilters,
    queryList,
    selectedCategories
  );

  const renderCategoryFilters = (compact = false) => (
    <>
      <div className={compact ? "border-b border-[#E6EAF0] pb-2" : "bg-white border-b-2 border-[#E6EAF0] pb-2"}>
        <button
          type="button"
          className="flex w-full items-center justify-between px-1 py-2 text-left"
          onClick={() => setCategoryOpen((v) => !v)}
        >
          <span
            className={`rounded-full bg-[#7352C7] px-3 py-1 font-semibold text-white ${
              compact ? "text-[12px]" : "text-[14px]"
            }`}
          >
            Category
          </span>
          <span className="text-[#6E7B86]">{categoryOpen ? "▴" : "▾"}</span>
        </button>
        {categoryOpen && (
          <div
            className={`space-y-2 px-3 pb-3 text-[#6E7B86] ${compact ? "px-2 pb-2 text-[14px]" : "text-[16px]"}`}
          >
            {categoryOptions.length === 0 && (
              <div className="text-[12px] text-slate-400">No categories</div>
            )}
            {categoryOptions.map((c) => (
              <label key={c} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedCategories.has(c)}
                  onChange={(e) => {
                    const { categories, subcategories } = toggleCatalogCategorySelection({
                      selectedCategories,
                      selectedSubcategories,
                      category: c,
                      checked: e.target.checked,
                      catalog: catalogForFilters,
                    });
                    setSelectedCategories(categories);
                    setSelectedSubcategories(subcategories);
                  }}
                  className="h-3.5 w-3.5 accent-[#7352C7]"
                  style={{ accentColor: "#7352C7" }}
                />
                <span>{c}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className={compact ? "pb-2" : "bg-white pb-2"}>
        <button
          type="button"
          className="flex w-full items-center justify-between px-1 py-2 text-left"
          onClick={() => setSubCategoryOpen((v) => !v)}
        >
          <span
            className={`rounded-full bg-[#7352C7] px-3 py-1 font-semibold text-white ${
              compact ? "text-[12px]" : "text-[14px]"
            }`}
          >
            Sub Category
          </span>
          <span className="text-[#6E7B86]">{subCategoryOpen ? "▴" : "▾"}</span>
        </button>
        {subCategoryOpen && (
          <div
            className={`space-y-2 px-3 pb-3 text-[#6E7B86] ${compact ? "px-2 pb-2 text-[14px]" : "text-[16px]"}`}
          >
            {subcategoryOptions.length === 0 && (
              <div className="text-[12px] text-slate-400">No sub categories</div>
            )}
            {subcategoryOptions.map((sc) => (
              <label key={sc} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedSubcategories.has(sc)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const next = new Set(selectedSubcategories);
                    if (checked) next.add(sc);
                    else next.delete(sc);
                    setSelectedSubcategories(next);
                  }}
                  className="h-3.5 w-3.5 accent-[#7352C7]"
                  style={{ accentColor: "#7352C7" }}
                />
                <span>{sc}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </>
  );

  const renderActionButtonsSection = () => {
    if (!showProductGrid) return null;
    const actionButtonClass =
      "inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-[#7352C7] px-3 py-1.5 text-[13px] font-bold text-white shadow-sm sm:px-4 sm:text-[15px]";
    const upgradeButtonClass =
      "inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full border-2 border-[#7352C7] bg-white px-3 py-1.5 text-[13px] font-bold text-[#7352C7] shadow-sm sm:px-4 sm:text-[15px]";
    return (
      <div className="flex w-full min-w-0 flex-col gap-1.5 md:w-auto md:shrink-0 md:items-end">
        <div className="w-full min-w-0 overflow-x-auto overscroll-x-contain px-0.5 py-1.5 md:overflow-visible md:px-0 md:py-0">
          <div className="flex w-max items-start gap-2 pr-1 md:ml-auto md:pr-0">
            <button
              type="button"
              onClick={() => navigate("/settings?tab=grocery")}
              className={actionButtonClass}
            >
              My Grocery List
            </button>
            {!isOfferMode && (
              <>
                <div
                  className={`relative flex shrink-0 flex-col${
                    isBasketSummaryDisabled ? " cursor-not-allowed p-0.5" : ""
                  }`}
                >
                  <button
                    type="button"
                    onClick={handleContinue}
                    disabled={isBasketSummaryDisabled || profileLoading}
                    aria-describedby={
                      isBasketSummaryDisabled &&
                      isLoggedIn &&
                      user &&
                      comparisonUsage &&
                      !comparisonUsage.unlimited
                        ? "basket-summary-comparison-status"
                        : undefined
                    }
                    aria-label={
                      isBasketSummaryDisabled
                        ? `See Cheapest Basket — ${comparisonLimitMessage}`
                        : "See Cheapest Basket"
                    }
                    className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-[#0d9499] px-3 py-1.5 text-[13px] font-bold text-white shadow-sm disabled:cursor-not-allowed sm:px-4 sm:text-[15px]${
                      isBasketSummaryDisabled
                        ? " bg-[#0d9499]/45 text-white/85 ring-2 ring-[#C62828] ring-offset-1"
                        : " disabled:opacity-60"
                    }`}
                  >
                    See Cheapest Basket
                  </button>
                  {isBasketSummaryDisabled &&
                    isLoggedIn &&
                    user &&
                    comparisonUsage &&
                    !comparisonUsage.unlimited && (
                      <div className="relative mt-3 w-max max-w-[220px]" role="alert">
                        <span
                          className="absolute -top-1.5 left-6 h-2.5 w-2.5 rotate-45 bg-[#C62828]"
                          aria-hidden="true"
                        />
                        <p
                          id="basket-summary-comparison-status"
                          className="rounded-md bg-[#C62828] px-3 py-2 text-left text-[13px] font-medium leading-snug text-white shadow-md sm:text-[14px]"
                        >
                          {formatComparisonCount(comparisonUsage.remaining)} comparison
                          {comparisonUsage.remaining === 1 ? "" : "s"} available
                        </p>
                      </div>
                    )}
                  {!isBasketSummaryDisabled &&
                    isLoggedIn &&
                    user &&
                    comparisonUsage &&
                    !comparisonUsage.unlimited && (
                      <p className="mt-1 whitespace-nowrap text-center text-[14px] font-normal text-[#6E7B86] md:text-[16px]">
                        {formatComparisonCount(comparisonUsage.remaining)} comparison
                        {comparisonUsage.remaining === 1 ? "" : "s"} available
                      </p>
                    )}
                  {!isBasketSummaryDisabled && isLoggedIn && user && comparisonUsage?.unlimited && (
                    <p className="mt-1 whitespace-nowrap text-center text-[16px] font-normal text-[#6E7B86]">
                      Unlimited comparisons available
                    </p>
                  )}
                </div>
                {isBasketSummaryDisabled && (
                  <button
                    type="button"
                    onClick={() => navigate("/subscription")}
                    className={upgradeButtonClass}
                  >
                    Upgrade plan
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const closeListPicker = useCallback(() => {
    listPickerResolver?.(null);
    setListPickerOpen(false);
    setListPickerOptions([]);
    setListPickerResolver(null);
  }, [listPickerResolver]);

  const chooseList = useCallback((lists, { title } = {}) => {
    return new Promise((resolve) => {
      setListPickerTitle(title || "Choose Grocery List");
      setListPickerOptions(lists);
      setListPickerResolver(() => resolve);
      setListPickerOpen(true);
    });
  }, []);

  const resolveGroceryListForCompare = useCallback(async () => {
    const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
    if (!minimalRes.ok) {
      const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
      throw new Error(
        errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists."
      );
    }

    const lists = normalizeGroceryListsMinimal(minimalRes.data);
    if (!lists.length) {
      throw new Error("No grocery list found. Please create a list first.");
    }

    if (lists.length === 1) {
      const listId = groceryListIdOf(lists[0]);
      if (!listId) throw new Error("Unable to select grocery list.");
      return String(listId);
    }

    const selected = await chooseList(lists, {
      title: "Choose Grocery List to Compare",
    });
    if (!selected) {
      const cancelError = new Error("LIST_PICKER_CANCELLED");
      cancelError.code = "LIST_PICKER_CANCELLED";
      throw cancelError;
    }

    const listId = groceryListIdOf(selected);
    if (!listId) throw new Error("Unable to select grocery list.");
    return String(listId);
  }, [chooseList]);

  useEffect(() => {
    if (!listPickerOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") closeListPicker();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [listPickerOpen, closeListPicker]);

  useEffect(() => {
    const flow = loadPasteListFlow();
    if (!flow?.lines?.length) {
      navigate("/paste-list", { replace: true });
      return;
    }
    const restoredMode = resolvePasteListPriceMode(flow.priceMode);
    setPriceMode(restoredMode);
    setLines(expandPasteLines(flow.rawText || flow.lines.join("\n")));
    setSelectedCategories(toFilterSet(flow.selectedCategories));
    setSelectedSubcategories(toFilterSet(flow.selectedSubcategories));
    setFlowReady(true);
  }, [navigate]);

  useEffect(() => {
    if (!isLoggedIn || user || authStatus === "loading") return;
    dispatch(fetchCurrentUser());
  }, [isLoggedIn, user, authStatus, dispatch]);

  useEffect(() => {
    if (!flowReady) return;
    const flow = loadPasteListFlow();
    if (!flow) return;
    savePasteListFlow({
      ...flow,
      selectedCategories: Array.from(selectedCategories),
      selectedSubcategories: Array.from(selectedSubcategories),
    });
  }, [flowReady, selectedCategories, selectedSubcategories]);

  useEffect(() => {
    if (!flowReady || status !== "succeeded" || !lines.length) return;
    const flow = loadPasteListFlow();
    if (!flow) return;
    savePasteListFlow({
      ...flow,
      rawText: flow.rawText || (flow.lines || []).join("\n"),
      queryList,
      products: preparedDisplayItems,
      totalCount: totalProductsCount,
      priceMode,
      selectedCategories: Array.from(selectedCategories),
      selectedSubcategories: Array.from(selectedSubcategories),
    });
  }, [
    flowReady,
    status,
    lines.length,
    preparedDisplayItems,
    totalProductsCount,
    queryList,
    priceMode,
    selectedCategories,
    selectedSubcategories,
  ]);

  const proceedToCheapestBasket = useCallback(
    async (groceryListId) => {
      const listId = groceryListId == null ? "" : String(groceryListId).trim();
      if (!listId) {
        showToast({
          type: "error",
          title: "List unavailable",
          message: "Grocery list id is missing.",
        });
        return false;
      }

      const { detail, products: productsForBasket } = await fetchGroceryListDetailProducts(listId);
      if (!productsForBasket.length) {
        showToast({
          type: "error",
          title: "Empty list",
          message: "Add products to this grocery list before comparing baskets.",
        });
        return false;
      }

      const { basketTotals, basketComparison } = await fetchGroceryBasketSummary(listId);
      if (basketComparison) {
        dispatch(updateBasketComparisonUsage(basketComparison));
      }

      if (!basketTotals?.rows?.length) {
        showToast({
          type: "error",
          title: "No basket totals",
          message: "Could not calculate store totals for this grocery list yet.",
        });
        return false;
      }

      const flow = loadPasteListFlow();
      savePasteListFlow({
        ...(flow || {}),
        source: "paste",
        fromPasteList: true,
        groceryListId: listId,
        products: productsForBasket,
        basketTotals,
        totalCount: productsForBasket.length,
        itemCount: productsForBasket.length,
        lines: flow?.lines || lines,
        rawText: flow?.rawText || (flow?.lines || lines || []).join("\n"),
        queryList,
        priceMode,
        selectedCategories: Array.from(selectedCategories),
        selectedSubcategories: Array.from(selectedSubcategories),
        groceryListTitle: detail?.title || detail?.name || "",
      });
      navigate("/paste-list/summary", {
        state: { fromPasteList: true, groceryListId: listId },
      });
      return true;
    },
    [
      dispatch,
      showToast,
      lines,
      queryList,
      priceMode,
      selectedCategories,
      selectedSubcategories,
      navigate,
    ]
  );

  const openLimitModal = (message) => {
    setLimitModalMessage(message || comparisonLimitMessage);
    setLimitModalOpen(true);
  };

  const handleContinue = async () => {
    if (!isLoggedIn) {
      onOpenAuth?.("login");
      return;
    }
    if (profileLoading) return;
    if (!user) {
      dispatch(fetchCurrentUser());
      return;
    }

    if (!canStartComparison) {
      openLimitModal(comparisonLimitMessage);
      return;
    }

    let latestProfile = user;
    try {
      latestProfile = await dispatch(fetchCurrentUser({ force: true })).unwrap();
    } catch {
      // Use cached profile if refresh fails.
    }

    const canCompareNow =
      latestProfile?.allowed === true && latestProfile?.can_compare === true;
    if (!canCompareNow) {
      openLimitModal(comparisonLimitMessage);
      return;
    }

    try {
      const listId = await resolveGroceryListForCompare();
      setPendingGroceryListId(listId);
      setCompareConfirmOpen(true);
    } catch (err) {
      if (err?.code === "LIST_PICKER_CANCELLED") return;
      showToast({
        type: "error",
        title: "Grocery list unavailable",
        message: err?.message || "Could not select a grocery list to compare.",
      });
    }
  };

  const handleConfirmCompare = useCallback(async () => {
    if (!pendingGroceryListId) {
      showToast({
        type: "error",
        title: "List unavailable",
        message: "Please select a grocery list to compare.",
      });
      return;
    }

    setCompareSubmitting(true);
    try {
      await proceedToCheapestBasket(pendingGroceryListId);
      setCompareConfirmOpen(false);
      setPendingGroceryListId(null);
    } catch (err) {
      const message =
        err?.data?.errors?.[0] ||
        err?.message ||
        "Failed to calculate basket summary.";
      if (err?.data?.basket_comparison) {
        dispatch(updateBasketComparisonUsage(err.data.basket_comparison));
      } else if (err?.data) {
        dispatch(updateBasketComparisonUsage(err.data));
      }
      setCompareConfirmOpen(false);
      if (
        /limit|compar/i.test(String(message)) ||
        err?.status === 403 ||
        err?.data?.can_compare === false ||
        err?.data?.allowed === false
      ) {
        openLimitModal(message);
      } else {
        showToast({
          type: "error",
          title: "Basket summary failed",
          message,
        });
      }
    } finally {
      setCompareSubmitting(false);
    }
  }, [dispatch, openLimitModal, pendingGroceryListId, proceedToCheapestBasket, showToast]);

  const handleAddToList = useCallback(
    async (product) => {
      const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
      if (!minimalRes.ok) {
        const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
        throw new Error(errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists.");
      }

      const lists = normalizeGroceryListsMinimal(minimalRes.data);

      if (!lists.length) throw new Error("No grocery list found. Please create a list first.");

      let selectedListId = null;
      if (lists.length === 1) {
        selectedListId = groceryListIdOf(lists[0]);
      } else {
        const selected = await chooseList(lists);
        if (!selected) {
          const cancelError = new Error("ADD_TO_LIST_CANCELLED");
          cancelError.code = "ADD_TO_LIST_CANCELLED";
          throw cancelError;
        }
        selectedListId = groceryListIdOf(selected);
      }
      if (!selectedListId) throw new Error("Unable to select grocery list.");

      const productId = product?.product_id ?? product?.pid ?? product?.sku ?? null;
      if (!productId) throw new Error("Invalid product.");

      const addRes = await apiRequest(`api/grocery_lists/${selectedListId}/grocery_products`, {
        method: "POST",
        body: buildGroceryAddToListBody(product),
      });
      if (!addRes.ok) {
        const errors = Array.isArray(addRes.data?.errors) ? addRes.data.errors : null;
        throw new Error(errors?.join("\n") || addRes.data?.message || "Failed to add product to list.");
      }

      rememberGroceryProductView({
        listId: selectedListId,
        productId: String(productId),
        view: "compare",
      });
    },
    [chooseList]
  );

  return (
    <div className="flex min-h-screen flex-col bg-[#F5F7FB] text-[#24303A]">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />
      <PasteListFlowHeader currentStep={2} />

      <main className="mx-auto w-full max-w-7xl flex-1 px-3 py-5 sm:px-4 sm:py-6 md:px-6">
        <div className="mb-5 flex min-w-0 items-start justify-between gap-4 rounded-2xl border border-[#E6EAF0] bg-white p-4 shadow-sm sm:items-center sm:p-5">
          <div className="min-w-0 flex-1">
            <h2 className="text-[20px] font-semibold text-[#24303A] sm:text-[22px]">Review Matches</h2>
            <p className="mt-1 text-[14px] text-[#6E7B86] sm:text-[16px]">
              Showing {displayItems.length} of {totalProductsCount} products
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const flow = loadPasteListFlow();
              if (flow) {
                savePasteListFlow({
                  ...flow,
                  rawText: flow.rawText || (flow.lines || []).join("\n"),
                });
              }
              navigate("/paste-list", { state: { fromEditList: true } });
            }}
            className="shrink-0 rounded-full border border-[#E6EAF0] bg-[#7352C7] px-5 py-1.5 text-[14px] font-semibold text-[#fff] shadow-sm sm:py-1.5 sm:text-[16px]"
          >
            Edit list
          </button>
        </div>

        <div className="mb-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-[18px] font-semibold text-[#24303A]">Price Mode</h3>
            <CatalogFilterButton
              count={selectedCategories.size + selectedSubcategories.size}
              onClick={() => setMobileFiltersOpen(true)}
              className="md:hidden"
            />
          </div>
          <div className="flex min-w-0 flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4">
            <div className="flex flex-wrap items-center gap-2">
              {PRICE_MODE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handlePriceModeChange(option.id)}
                  className={priceModeChipClass(priceMode === option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {renderActionButtonsSection()}
          </div>
        </div>

        <div className="flex min-w-0 gap-4 md:gap-6">
          <aside className="sticky top-24 hidden h-[calc(100vh-90px)] w-64 shrink-0 rounded-2xl border border-[#E6EAF0] bg-white p-4 text-[16px] shadow-sm md:block">
            <div className="mb-4 flex items-center justify-end border-b-2 border-[#E6EAF0] pb-4">
              <button
                type="button"
                onClick={resetFilters}
                className="rounded-full bg-[#7352C7] px-3 py-1 text-[16px] font-semibold text-white shadow-sm"
              >
                Reset
              </button>
            </div>
            <div className="h-[calc(100%-56px)] space-y-4 overflow-y-auto pr-2">{renderCategoryFilters()}</div>
          </aside>

          <div className="min-w-0 flex-1 space-y-4">
            {isInitialLoading && !error && (
              <div className="flex justify-center py-16">
                <span className="text-[16px] text-[#6E7B86]">Loading products...</span>
              </div>
            )}

            {(error || status === "failed") && !isLoadMoreError && (
              <ListFetchError message={error} onRetry={retryFetch} isLoading={status === "loading"} />
            )}

            {showProductGrid && (
              <div
                className={`grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3${
                  status === "loading" && isLoadingMore
                    ? ""
                    : status === "loading"
                    ? " pointer-events-none opacity-60"
                    : ""
                }`}
              >
                {displayItems.map((product, idx) => (
                  <div
                    key={`${product.pid || product.sku}-${idx}`}
                    ref={idx === prefetchIndex ? bindPrefetchNode : undefined}
                    className="min-w-0"
                  >
                    <ProductCard
                      accent={["orange", "blue", "green", "purple"][idx % 4]}
                      product={product}
                      pricingMode={isOfferMode ? "Loyalty+Offer" : "Base"}
                      onAddToList={handleAddToList}
                      onClick={() => {
                        const productId = product.pid || product.sku;
                        if (!productId) return;
                        try {
                          window.sessionStorage.setItem(
                            `product-detail-cache:${productId}`,
                            JSON.stringify(product)
                          );
                        } catch {
                          // ignore cache write failures
                        }
                        navigate(`/product/${encodeURIComponent(productId)}`, {
                          state: {
                            product,
                            pricingMode: isOfferMode ? "Loyalty+Offer" : "Base",
                            offer: isOfferMode,
                          },
                        });
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

            {!error && status !== "loading" && displayItems.length === 0 && (
              <div className="flex justify-center py-16">
                <span className="text-[16px] text-slate-500">No products found.</span>
              </div>
            )}

            {(hasMore || isLoadingMore) && !isLoadMoreError && (
              <InfiniteScrollSentinel isLoading={isLoadingMore} hasMore={hasMore} />
            )}

            {isLoadMoreError && (
              <ListFetchError message={error} onRetry={retryFetch} isLoading={status === "loading"} />
            )}
          </div>
        </div>
      </main>

      <Footer />

      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-[80] md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close filters"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="absolute right-0 top-0 flex h-full max-h-[100dvh] w-[88%] max-w-sm flex-col border-l border-[#E6EAF0] bg-white shadow-xl">
            <div className="flex shrink-0 items-center justify-end border-b border-[#E6EAF0] p-4 pb-3">
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#E6EAF0] text-slate-600"
                aria-label="Close filters panel"
              >
                ✕
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 pb-2 pt-2">
              {renderCategoryFilters(true)}
            </div>
            <div className="shrink-0 border-t border-[#E6EAF0] bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    resetFilters();
                    setMobileFiltersOpen(false);
                  }}
                  className="flex-1 rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[14px] font-semibold text-[#24303A]"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="flex-1 rounded-full bg-[#7352C7] px-4 py-2 text-[14px] font-semibold text-white"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {listPickerOpen && (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close list selector"
            onClick={closeListPicker}
          />
          <div className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-xl">
              <h3 className="text-base font-bold text-[#24303A]">{listPickerTitle}</h3>
              <div className="mt-4 space-y-2">
                {listPickerOptions.map((list, index) => {
                  const listId = list?.id ?? list?.grocery_list_id ?? list?.list_id ?? "";
                  const listTitle = list?.title || `List ${index + 1}`;
                  return (
                    <button
                      key={`${listId}-${index}`}
                      type="button"
                      className="w-full rounded-xl border border-[#E6EAF0] bg-white px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        listPickerResolver?.(list);
                        setListPickerOpen(false);
                        setListPickerOptions([]);
                        setListPickerResolver(null);
                      }}
                    >
                      <p className="text-[16px] font-semibold text-slate-800">{listTitle}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="rounded-lg border border-[#E6EAF0] bg-white px-3 py-1.5 text-[16px] font-semibold text-slate-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {compareConfirmOpen && (
        <div className="fixed inset-0 z-[90]">
          <button
            type="button"
            aria-label="Close compare confirmation"
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!compareSubmitting) {
                setCompareConfirmOpen(false);
                setPendingGroceryListId(null);
              }
            }}
          />
          <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
            <div className="relative w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900">Compare basket?</h3>
              <p className="mt-2 text-[16px] text-slate-600">
                Are you sure you want to compare?
                {comparisonUsage?.unlimited ? null : (
                  <> This will use 1 of your monthly basket comparisons.</>
                )}
              </p>
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  disabled={compareSubmitting}
                  onClick={() => {
                    if (!compareSubmitting) {
                      setCompareConfirmOpen(false);
                      setPendingGroceryListId(null);
                    }
                  }}
                  className="flex-1 rounded-full border border-slate-300 bg-white px-4 py-2 text-[16px] font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  No
                </button>
                <button
                  type="button"
                  disabled={compareSubmitting}
                  onClick={handleConfirmCompare}
                  className="flex-1 rounded-full bg-[#7352C7] px-4 py-2 text-[16px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {compareSubmitting ? "Starting…" : "Yes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {limitModalOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-6 text-center shadow-xl">
            <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-amber-400 text-amber-500">
              <span className="text-[20px] font-bold">!</span>
            </div>
            <h3 className="text-[20px] font-bold text-[#24303A] sm:text-[22px]">Comparison limit</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-[#6E7B86] sm:text-[16px]">
              {limitModalMessage || comparisonLimitMessage}
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => setLimitModalOpen(false)}
                className="rounded-full border border-[#E6EAF0] bg-white px-6 py-2 text-[15px] font-semibold text-[#24303A] sm:text-[16px]"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  setLimitModalOpen(false);
                  navigate("/subscription");
                }}
                className="rounded-full bg-[#7352C7] px-6 py-2 text-[15px] font-semibold text-white shadow-sm sm:text-[16px]"
              >
                Upgrade plan
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default PasteListReview;
